import { Notice } from '@genial/components';
import { Utils } from '@genial/utils';
import React, { useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { CustomerGlobalSearchResponseNormalized } from '../../../../../../../store/customer/types';
import { fixedFeeCustomerTermReplacementStatusQuery } from '../../../../../../../store/fixedFee/selector';
import { FixedFeeCustomerTermReplacementStatus } from '../../../../../../../store/fixedFee/types';
import {
  OVERWRITE_TERM_NOTICE_MESSAGE,
  OVERWRITE_TERM_RESTRICTED_NOTICE_MESSAGE,
} from '../../../../constants';

interface OverwriteTermNoticeProps {
  selectedCustomer?: CustomerGlobalSearchResponseNormalized;
  adjustedNetWorth?: number;
  rate?: number;
  isNetWorthLoading: boolean;
  onTermReplacementRestrictionChange: (isRestricted: boolean, isLoading: boolean) => void;
}

export const OverwriteTermNotice: React.FC<OverwriteTermNoticeProps> = ({
  rate,
  selectedCustomer,
  adjustedNetWorth,
  isNetWorthLoading,
  onTermReplacementRestrictionChange,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [loadedData, setLoadedData] = useState<FixedFeeCustomerTermReplacementStatus>();
  const canQueryTermReplacementStatus =
    !!selectedCustomer && !isNetWorthLoading && adjustedNetWorth !== undefined;

  const { data, loading } = useRecoilQuery(
    fixedFeeCustomerTermReplacementStatusQuery({
      accountNumber: selectedCustomer?.accountNumber || 0,
      positionTotalValueInvested: adjustedNetWorth || 0,
      feePercentage: rate || 0,
    }),
    {
      if: () => canQueryTermReplacementStatus && !loaded,
    }
  );

  useEffect(() => {
    if (loaded) {
      return;
    }
    if (!canQueryTermReplacementStatus) {
      onTermReplacementRestrictionChange(false, false);
      return;
    }

    if (loading) {
      onTermReplacementRestrictionChange(false, true);
      return;
    }

    if (!data) {
      onTermReplacementRestrictionChange(false, true);
      return;
    }

    setLoaded(true);
    setLoadedData(data);
    const isRestricted = !data.canCreate;

    onTermReplacementRestrictionChange(isRestricted, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, onTermReplacementRestrictionChange]);

  useEffect(() => {
    setLoaded(false);
  }, [selectedCustomer]);

  if (!canQueryTermReplacementStatus || loading || !loadedData) {
    return null;
  }

  if (loadedData?.canCreate && !loadedData?.canReplace) {
    return null;
  }

  const message = loadedData.canReplace
    ? OVERWRITE_TERM_NOTICE_MESSAGE
    : OVERWRITE_TERM_RESTRICTED_NOTICE_MESSAGE;

  return (
    <Notice
      backgroundColor={Utils.ColorToken.WithOpacity('feedback.attention.lightest', 0.4)}
      textObjects={[
        { text: 'Atenção: ', variant: 'bold' },
        { text: message, variant: 'regular' },
      ]}
      type="warning"
    />
  );
};
